import type { FC } from 'react';
import { Fragment } from 'react';
import { FieldSchemaIdTooltip } from '@components/FieldCdrId';
import Icon from '@components/Icon';
import IconMdi from '@components/IconMdi';
import { Show } from '@components/Show';
import Tooltip from '@components/Tooltip';
import { TooltipedIcon } from '@components/TooltipedIcon';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import { useFieldSchemaIds } from '@hooks/use-schema-ids';
import { mdiHelpCircleOutline, mdiMinus, mdiPlus, mdiTrashCan } from '@mdi/js';
import { resolveSchema, ROOT_SECTIONS_KEY } from '@root/helpers/schema';
import type { JSONSchema } from '@root/helpers/schema/types';
import mapChildren from '@root/src/mapChildren';
import clsx from 'clsx';
import { cloneDeep, get } from 'lodash-es';

import Button from '../../Button';
import Card from '../../Card';
import ErrorText from '../../ErrorText';
import H4 from '../../H4';

import type { SectionRepeaterViewProps } from './types';

export const SectionRepeaterView: FC<SectionRepeaterViewProps> = ({
  autoGrow,
  cdrId,
  cdrName,
  mrcId,
  handleRemove,
  title,
  emptyContent,
  helperText,
  initialItems,
  schemaItem,
  className,
  id,
  item,
  ownKey,
  validationErrors,
  showQuestionKey,
  onChange,
  isReadOnly = false,
  isDisabled,
  total100Error,
  canAdd,
  handleAdd,
  hideErrors,
}) => {
  const isSections = ownKey === ROOT_SECTIONS_KEY;
  const formValues = useDatapointsStore.getState().formValues;
  const shouldHideTitle = get(item, 'ui:hideTitle', false);
  const { schemaId, parsedCdrIds, parsedCdrNames } = useFieldSchemaIds({ cdrId, cdrName, mrcId });
  return (
    <div className={clsx('mb-5 rounded-lg p-6 py-6', className)}>
      <Show when={!shouldHideTitle}>
        <FieldSchemaIdTooltip schemaId={schemaId} cdrIds={parsedCdrIds} cdrNames={parsedCdrNames}>
          <H4 className="flex items-center justify-between">
            <div className="flex justify-start gap-2">
              {title}
              {helperText && (
                <TooltipedIcon
                  path={mdiHelpCircleOutline}
                  data-testid="select-variation"
                  placement="top"
                  content={helperText}
                />
              )}
            </div>
          </H4>
        </FieldSchemaIdTooltip>
      </Show>
      {emptyContent && (
        <div className="bg-info-50 text-info-600 mb-4 flex items-center gap-x-3.5 rounded-md px-3 py-4 text-xs font-medium">
          <Icon name="book" />
          {emptyContent}
        </div>
      )}

      {initialItems.map((_lineItem, index) => {
        const key = `${ownKey}[${index}]`;
        const clonedSchema = cloneDeep(schemaItem);

        resolveSchema(clonedSchema, get(formValues, key));

        return (
          <Fragment key={key}>
            <Card
              className="mb-8"
              header={
                <Card.Header className="flex items-center justify-between p-0">
                  <div className="font-medium">
                    {item.items?.title} #{index + 1}
                  </div>
                  <Show when={isSections && !isReadOnly}>
                    <div className="flex items-center justify-end">
                      <Show when={canAdd}>
                        <Tooltip content="Add new above">
                          <Button
                            onClick={() => handleAdd(index)}
                            kind="primary"
                            className="h-7 p-1"
                            isDisabled={isDisabled}
                          >
                            <IconMdi path={mdiPlus} />
                          </Button>
                        </Tooltip>
                      </Show>
                      <Show
                        when={
                          (item?.minimum === undefined || item?.minimum < initialItems.length) &&
                          initialItems.length > 0
                        }
                      >
                        <Tooltip content="Remove">
                          <Button
                            className="bg-info-500 hover:!bg-info-600 ml-2 h-7 border-none p-1 font-normal text-white"
                            data-testid={`${id}-remove-section-button`}
                            onClick={() => handleRemove(index)}
                            isDisabled={isDisabled}
                          >
                            <IconMdi path={mdiTrashCan} />
                          </Button>
                        </Tooltip>
                      </Show>
                    </div>
                  </Show>
                </Card.Header>
              }
            >
              {mapChildren({
                autoGrow,
                onChange,
                parentKey: key,
                parentSchema: clonedSchema as JSONSchema,
                validationErrors,
                showQuestionKey,
                isReadOnly,
                isDisabled,
                hideErrors,
              })}
            </Card>
          </Fragment>
        );
      })}
      {total100Error && <ErrorText className="mb-4">{total100Error.message}</ErrorText>}
      <div className="flex">
        <Show
          when={
            !isSections &&
            !isReadOnly &&
            (item?.minimum === undefined || item?.minimum < initialItems.length) &&
            initialItems.length > 0
          }
        >
          <Button
            className="mr-4 h-10"
            kind="danger"
            data-testid={`${id}-remove-section-button`}
            onClick={() => handleRemove(initialItems.length - 1)}
            isDisabled={isDisabled}
          >
            <IconMdi path={mdiMinus} />
            {`Remove ${item.title ?? 'a section'}`}
          </Button>
        </Show>
        <Show when={!isReadOnly && canAdd}>
          <div className="flex">
            <Button
              onClick={() => handleAdd(initialItems.length)}
              kind="primary"
              className="h-10"
              isDisabled={isDisabled}
            >
              <IconMdi path={mdiPlus} />
              {`Add ${item.title ?? 'a section'}`}
            </Button>
          </div>
        </Show>
      </div>
    </div>
  );
};
